class StreamRequest {
  constructor(options = {}) {
    this.options = {
      url: '',
      method: 'POST',
      responseType: 'arraybuffer',
      data: {},
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream, application/json'
      },
      onMessage: () => {},
      onComplete: () => {},
      onError: () => {},
      onOpen: () => {},
      timeout: 30000,
      delimiter: '\n\n',  // SSE默认分隔符
      ...options
    };
    
    this.isFinished = false;
    this.buffer = '';
    this.eventSource = null;
    this.xhr = null;
    this.pendingBytes = [];
  }

  decodeUtf8(input, stream = false) {
    if (typeof TextDecoder !== 'undefined') {
      if (!this.textDecoder) {
        this.textDecoder = new TextDecoder('utf-8');
      }
      return this.textDecoder.decode(input, { stream });
    }

    const bytes = Array.prototype.slice.call(new Uint8Array(input));
    const data = this.pendingBytes.concat(bytes);
    const chars = [];
    let i = 0;

    while (i < data.length) {
      const byte1 = data[i];
      let bytesNeeded = 0;
      let codePoint = 0;

      if (byte1 <= 0x7f) {
        chars.push(String.fromCharCode(byte1));
        i += 1;
        continue;
      } else if (byte1 >= 0xc2 && byte1 <= 0xdf) {
        bytesNeeded = 1;
        codePoint = byte1 & 0x1f;
      } else if (byte1 >= 0xe0 && byte1 <= 0xef) {
        bytesNeeded = 2;
        codePoint = byte1 & 0x0f;
      } else if (byte1 >= 0xf0 && byte1 <= 0xf4) {
        bytesNeeded = 3;
        codePoint = byte1 & 0x07;
      } else {
        chars.push('\uFFFD');
        i += 1;
        continue;
      }

      if (i + bytesNeeded >= data.length) {
        if (stream) break;
        chars.push('\uFFFD');
        i += 1;
        continue;
      }

      let valid = true;
      for (let j = 1; j <= bytesNeeded; j++) {
        const byte = data[i + j];
        if ((byte & 0xc0) !== 0x80) {
          valid = false;
          break;
        }
        codePoint = (codePoint << 6) | (byte & 0x3f);
      }

      if (!valid) {
        chars.push('\uFFFD');
        i += 1;
        continue;
      }

      if (codePoint <= 0xffff) {
        chars.push(String.fromCharCode(codePoint));
      } else {
        codePoint -= 0x10000;
        chars.push(String.fromCharCode(0xd800 + (codePoint >> 10)));
        chars.push(String.fromCharCode(0xdc00 + (codePoint & 0x3ff)));
      }
      i += bytesNeeded + 1;
    }

    this.pendingBytes = stream ? data.slice(i) : [];
    return chars.join('');
  }

  // 真正的流式请求 - 使用 EventSource (SSE)
  async requestSSE() {
    if (typeof EventSource === 'undefined') {
      throw new Error('EventSource is not supported in this environment');
    }

    return new Promise((resolve, reject) => {
      try {
        // 构建带参数的URL
        let url = this.options.url;
        if (this.options.method === 'GET' && Object.keys(this.options.data).length > 0) {
          const params = new URLSearchParams(this.options.data).toString();
          url += (url.includes('?') ? '&' : '?') + params;
        }

        this.eventSource = new EventSource(url, {
          withCredentials: false
        });

        this.eventSource.onopen = () => {
          this.options.onOpen?.();
        };

        this.eventSource.onmessage = (event) => {
          if (!this.isFinished) {
            try {
              const data = event.data;
              if (data === '[DONE]') {
                this.eventSource.close();
                this.options.onComplete?.({ completed: true });
                resolve({ completed: true });
                this.isFinished = true;
              } else {
                this.options.onMessage?.(data);
              }
            } catch (err) {
              console.warn('Error processing SSE message:', err);
            }
          }
        };

        this.eventSource.onerror = (error) => {
          if (!this.isFinished) {
            this.eventSource.close();
            this.options.onError?.(error);
            reject(error);
            this.isFinished = true;
          }
        };

        // 设置超时
        if (this.options.timeout > 0) {
          setTimeout(() => {
            if (!this.isFinished) {
              this.abort();
              const timeoutError = new Error(`Request timeout after ${this.options.timeout}ms`);
              this.options.onError?.(timeoutError);
              reject(timeoutError);
            }
          }, this.options.timeout);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  // 使用 Fetch API 实现流式请求
  async requestFetchStream() {
    if (typeof fetch === 'undefined') {
      throw new Error('Fetch API is not supported in this environment');
    }
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
          const timeoutError = new Error(`Request timeout after ${this.options.timeout}ms`);
          this.options.onError?.(timeoutError);
          reject(timeoutError);
        }, this.options.timeout);

        const response = await fetch(this.options.url, {
          method: this.options.method,
          headers: this.options.headers,
          body: this.options.method !== 'GET' ? JSON.stringify(this.options.data) : null,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        this.options.onOpen?.();

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Response body is not readable');
        }

        this.pendingBytes = [];
        this.textDecoder = null;
        this.buffer = '';

        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              // 处理缓冲区中剩余的数据
              if (this.buffer.trim()) {
                this.options.onMessage?.(this.buffer);
              }
              this.options.onComplete?.({ completed: true });
              resolve({ completed: true });
              this.isFinished = true;
              break;
            }

            // 解码并添加到缓冲区
            const chunk = this.decodeUtf8(value, true);
            this.buffer += chunk;

            // 按分隔符分割处理完整消息
            const parts = this.buffer.split(this.options.delimiter);
            this.buffer = parts.pop() || ''; // 剩余部分放回缓冲区

            for (const part of parts) {
              if (part.trim()) {
                this.options.onMessage?.(part.trim());
              }
            }
          }
        } finally {
          reader.releaseLock();
        }

        this.abort = () => {
          controller.abort();
          reader.cancel();
          this.isFinished = true;
        };

      } catch (error) {
        if (error.name === 'AbortError') {
          // eslint-disable-next-line no-ex-assign
          error = new Error('Request was aborted');
        }
        this.options.onError?.(error);
        reject(error);
        this.isFinished = true;
      }
    });
  }

  // 自动选择最佳的流式请求方法
  async requestStream() {
    this.isFinished = false;
    this.buffer = '';
    
    // 检测环境并选择合适的方法
    if (typeof EventSource !== 'undefined' && this.options.headers['Accept']?.includes('text/event-stream')) {
      return this.requestSSE();
    } else if (typeof fetch !== 'undefined' && typeof ReadableStream !== 'undefined') {
      return this.requestFetchStream();
    } else {
      throw new Error('No supported streaming method available in this environment');
    }
  }

  // 中断请求
  abort() {
    this.isFinished = true;
    
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    
    if (this.xhr) {
      this.xhr.abort();
      this.xhr = null;
    }
  }

  // 重置状态
  reset() {
    this.isFinished = false;
    this.buffer = '';
    this.abort();
  }
}


// 导出类
export default StreamRequest;

